import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, LayoutDashboard, Map, Users, Zap, PieChart, GitCompare, 
  BookOpen, Settings, Shield, ArrowRight, Command, CornerDownLeft
} from 'lucide-react';
import { cn } from '../lib/utils';

const NAV_ITEMS = [
  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, section: 'Navigation' },
  { label: 'Simulations', path: '/simulations', icon: Map, section: 'Navigation' },
  { label: 'Populations', path: '/populations', icon: Users, section: 'Navigation' },
  { label: 'Stimuli', path: '/stimuli', icon: Zap, section: 'Navigation' },
  { label: 'Analysis', path: '/analysis', icon: PieChart, section: 'Navigation' },
  { label: 'Compare', path: '/compare', icon: GitCompare, section: 'Navigation' },
  { label: 'Guide', path: '/guide', icon: BookOpen, section: 'Navigation' },
  { label: 'Settings', path: '/settings', icon: Settings, section: 'Navigation' },
  { label: 'Admin Panel', path: '/admin', icon: Shield, section: 'Navigation' },
];

export const CommandPalette = ({ simulations = [], populations = [], stimuli = [] }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  // Build searchable items
  const allItems = useMemo(() => {
    const items = [...NAV_ITEMS];
    
    simulations?.slice(0, 20).forEach(sim => {
      items.push({
        label: sim.name,
        path: `/analysis?simulation=${sim.id}`,
        icon: Map,
        section: 'Simulations',
        meta: sim.status,
      });
    });

    populations?.slice(0, 20).forEach(pop => {
      items.push({
        label: pop.name,
        path: '/populations',
        icon: Users,
        section: 'Populations',
        meta: `${pop.individual_count || 0} individuals`,
      });
    });

    stimuli?.slice(0, 20).forEach(stim => {
      items.push({
        label: stim.name || stim.title,
        path: '/stimuli',
        icon: Zap,
        section: 'Stimuli',
        meta: stim.question_type || 'stimulus',
      });
    });

    return items;
  }, [simulations, populations, stimuli]);

  const filtered = useMemo(() => {
    if (!query.trim()) return allItems.slice(0, 12);
    const q = query.toLowerCase();
    return allItems.filter(item => 
      item.label?.toLowerCase().includes(q) || 
      item.section?.toLowerCase().includes(q) ||
      item.meta?.toLowerCase().includes(q)
    ).slice(0, 12);
  }, [query, allItems]);

  // Group by section
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(item => {
      if (!groups[item.section]) groups[item.section] = [];
      groups[item.section].push(item);
    });
    return groups;
  }, [filtered]);

  // Keyboard shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen(prev => !prev);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Focus input on open
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = useCallback((item) => {
    setOpen(false);
    navigate(item.path);
  }, [navigate]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[activeIndex]) handleSelect(filtered[activeIndex]);
    }
  };

  if (!open) return null;

  let flatIndex = -1;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm cmd-backdrop"
        onClick={() => setOpen(false)}
        data-testid="command-palette-backdrop"
      />
      
      {/* Palette */}
      <div 
        className="fixed inset-x-0 top-[15%] z-[61] mx-auto w-full max-w-[560px] cmd-panel"
        data-testid="command-palette"
      >
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-2xl shadow-black/20 overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-slate-100 dark:border-slate-800">
            <Search className="w-4.5 h-4.5 text-slate-400 flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setActiveIndex(0); }}
              onKeyDown={handleKeyDown}
              placeholder="Search simulations, populations, pages..."
              className="flex-1 h-12 bg-transparent text-sm text-slate-900 dark:text-white placeholder:text-slate-400 outline-none"
              data-testid="command-palette-input"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[320px] overflow-y-auto py-2 px-2">
            {filtered.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-slate-400">No results for "{query}"</p>
              </div>
            ) : (
              Object.entries(grouped).map(([section, items]) => (
                <div key={section}>
                  <p className="px-2 py-1.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    {section}
                  </p>
                  {items.map((item) => {
                    flatIndex++;
                    const idx = flatIndex;
                    const Icon = item.icon;
                    return (
                      <button
                        key={`${item.section}-${item.label}-${idx}`}
                        data-index={idx}
                        onClick={() => handleSelect(item)}
                        className={cn(
                          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors duration-75",
                          idx === activeIndex
                            ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300"
                            : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                        )}
                        data-testid={`command-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <div className={cn(
                          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
                          idx === activeIndex 
                            ? "bg-blue-100 dark:bg-blue-500/20" 
                            : "bg-slate-100 dark:bg-slate-800"
                        )}>
                          <Icon className="w-4 h-4" strokeWidth={1.75} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.label}</p>
                          {item.meta && (
                            <p className="text-xs text-slate-400 dark:text-slate-500 truncate">{item.meta}</p>
                          )}
                        </div>
                        {idx === activeIndex && (
                          <CornerDownLeft className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50">
            <div className="flex items-center gap-3 text-[11px] text-slate-400">
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded text-[10px]">↑↓</kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1 py-0.5 bg-slate-200 dark:bg-slate-700 rounded text-[10px]">↵</kbd>
                select
              </span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-slate-400">
              <Command className="w-3 h-3" />
              <span>K to toggle</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .cmd-backdrop {
          animation: cmd-fade-in 0.15s ease-out;
        }
        .cmd-panel {
          animation: cmd-slide-in 0.2s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes cmd-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cmd-slide-in {
          from { opacity: 0; transform: translateY(-12px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
};
